<template>
  <div class="chat">
    <Header
      v-if="!isMobile"
      :username="(interlocutor || {}).username"
      :avatar="(interlocutor || {}).avatar"
      :online="(interlocutor || {}).online"
    />

    <div class="chat__body">
      <div class="chat__body-history">
        <OverlayScrollbarsComponent ref="scroller" class="chat__body-history-scroller">
          <Message
            v-for="(message, index) in messages"
            :class="{ noDateTime: isEqualPrevDate(message.date, message.mine, index) }"
            :key="index"
            :mine="message.mine"
            :date="message.date"
            :content="message.content"
            :username="message.user.name"
          />

          <div class="chat__body-history-space"/>
        </OverlayScrollbarsComponent>
      </div>

      <textarea
        v-model="text"
        class="chat__body-textarea"
        :placeholder="$tCooperation('Your message') + '...'"
      />

      <div class="chat__body-buttons-wrapper" :class="{ isEmployer }">
        <button
          type="button"
          class="chat__body-send base-btn base-btn--grey-blue"
          @click="sendMessage()"
        >
          <span class="base-btn__text">{{$tCooperation('Submit message')}}</span>
        </button>

        <template v-if="isEmployer">
          <button
            type="button"
            class="chat__body-send base-btn base-btn--green"
            :class="{ '_loading': isLoading }"
            :disabled="stepIsReadonly"
            @click="!stepIsReadonly && $emit('finish')"
          >
            <BasePreloader v-if="isLoading || acceptPending"/>
            <span class="base-btn__text">{{$tCooperation('Accept candidate')}}</span>
          </button>

          <button
            type="button"
            class="chat__body-send base-btn base-btn--outline-orange"
            :class="{ '_loading': isLoading }"
            :disabled="stepIsReadonly"
            @click="!stepIsReadonly && $emit('decline')"
          >
            <BasePreloader v-if="isLoading"/>
            <span class="base-btn__text">{{$tCooperation('Deny candidate')}}</span>
          </button>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
  import { OverlayScrollbarsComponent } from 'overlayscrollbars-vue';
  import { http } from '../../axios';

  import Header from './Header';
  import Message from './Message';

  export default {
    components: { OverlayScrollbarsComponent, Header, Message },

    props: {
      isMobile: Boolean,
      interactionId: Number,
      stepIsReadonly: Boolean,
      isEmployer: Boolean,
      user: Object,
      acceptPending: Boolean,
      isLoading: Boolean
    },

    data() {
      return {
        text: '',
        error: null,
        interlocutor: null,

        messages: [],
        total: 0,
        perPage: 0,
        currentPage: 1
      };
    },

    computed: {
      scroller() {
        return ((this.$refs.scroller || {}).$el || {}).__overlayScrollbars__;
      }
    },

    watch: {
      interlocutor() {
        this.$emit('changed-interlocutor', this.interlocutor);
      }
    },

    methods: {
      loadMessages(page = this.currentPage) {
        return http.get(`/api/interaction/get-chat`, {
          params: {
            interaction_id: this.interactionId,
            page
          }
        }).then(({ data }) => {
          return this._loadMessages(data.data);
        }).catch((error) => {
          this.error = error.response && error.response.message || error.message;
        });
      },

      _loadMessages(data) {
        this.total = data.chat_messages.total;
        this.perPage = data.chat_messages.per_page;
        this.currentPage = data.chat_messages.current_page;

        const interlocutor = data.interlocutor;
        this.interlocutor = {
          ...interlocutor,
          username: `${interlocutor.firstname} ${interlocutor.lastname}`,
          online: true
        };

        return data.chat_messages.data.reverse().map((msg) => {
          const name = `${msg.customer.firstname} ${msg.customer.lastname}`;
          const mine = msg.customer_id === this.user.id;
          const date = new Date(msg.created_at_unix * 1000);

          return {
            ...msg,
            mine,
            date,
            content: msg.message,
            user: { name }
          };
        });
      },

      sendMessage(text) {
        if (!text && !this.text) return;

        if (!text) {
          text = this.text;
          this.text = '';
        }

        const index = this.messages.push({
          mine: true,
          content: text,
          date: new Date(),
          user: { name: `${this.user.firstname} ${this.user.lastname}` }
        }) - 1;

        this.scrollTo();

        return http.post(`/api/interaction/send-message`, null, {
          params: {
            interaction_id: this.interactionId,
            chat_message: text
          }
        })
          .then(({ data }) => {
            this.messages[ index ] = Object.assign(this.messages[ index ], data.data);
          })
          .catch((error) => {
            this.messages.splice(index, 1);
            this.error = error.response && error.response.message || error.message;
          });
      },

      async autoloadOldStart() {
        const isNeedToLoad = this.total > this.perPage * this.currentPage;
        if (!isNeedToLoad) return;

        if (this.scroller && this.scroller.scroll().position.y === 0) {
          const scrollHeight = this.scroller.getState().contentScrollSize.height;
          const newMessages = await this.loadMessages(this.currentPage + 1);

          this.messages = newMessages.concat(this.messages);
          setTimeout(() => this.scrollTo(this.scroller.getState().contentScrollSize.height - scrollHeight));
        }

        setTimeout(() => this.autoloadOldStart(), 100);
      },

      async autoloadNewStart() {
        if (this.scroller) {
          const isScrolledToBottom = this.scroller.scroll().position.y === (this.scroller.getState().contentScrollSize.height - this.scroller.getState().viewportSize.height);

          (await this.loadMessages(1)).forEach((msg) => {
            const isExists = this.messages.filter((m) => m.id === msg.id).length > 0;
            if (!isExists) this.messages.push(msg);
          });

          if (isScrolledToBottom) this.scrollTo();
        }

        setTimeout(() => this.autoloadNewStart(), 3000);
      },

      scrollTo(viewPos = null) {
        setTimeout(() => {
          if (!this.scroller) return;

          const y = viewPos !== null ? viewPos : '100%';
          this.scroller.scroll({ y });
        }, 10);
      },

      isEqualPrevDate(date, mine, index) {
        const prevMessage = this.messages[ index - 1 ] || {};
        const prevDate = prevMessage.date;
        if (!prevDate || !date || mine !== prevMessage.mine) return false;

        prevDate.setMilliseconds(0);
        prevDate.setSeconds(0);

        date.setMilliseconds(0);
        date.setSeconds(0);

        return prevDate.getTime() === date.getTime();
      }
    },

    async mounted() {
      this.$emit('init', this);

      this.error = null;
      this.messages = await this.loadMessages();

      this.scrollTo();
      setTimeout(() => {
        this.autoloadOldStart();
        // this.autoloadNewStart();
      }, 100);
    }
  };
</script>
